// Render a date as dd/mm/yy.
// Accepts dd/mm/yyyy (the project's canonical document-date string), ISO
// strings, SQLite 'YYYY-MM-DD HH:MM:SS' timestamps, or Date objects.
// Returns null for null/undefined/empty input. If parsing fails, returns the
// original input so we never silently lose data.
export function formatDateShort(input) {
  if (input === null || input === undefined || input === '') return null;

  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    const dd = String(input.getDate()).padStart(2, '0');
    const mm = String(input.getMonth() + 1).padStart(2, '0');
    const yy = String(input.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  }

  const s = String(input).trim();

  // dd/mm/yyyy or dd/mm/yy or d/m/yyyy
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slash) {
    const dd = slash[1].padStart(2, '0');
    const mm = slash[2].padStart(2, '0');
    const yy = slash[3].slice(-2);
    return `${dd}/${mm}/${yy}`;
  }

  // dd.mm.yyyy
  const dot = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (dot) {
    const dd = dot[1].padStart(2, '0');
    const mm = dot[2].padStart(2, '0');
    const yy = dot[3].slice(-2);
    return `${dd}/${mm}/${yy}`;
  }

  // ISO / SQLite timestamp — let Date parse it (UTC for the SQLite form).
  const iso = s.includes('T') || !s.includes(' ') ? s : s.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  }

  return s;
}

// ── Weekday-qualified dates ─────────────────────────────────────────────────
// 'יום א׳ 30/08/26' — how a date RANGE reads best in Hebrew, where "until
// Saturday" carries more than "until the 5th". Sunday–Friday are lettered and
// Saturday is named, as on any Israeli calendar.
const HEBREW_DAYS = ['יום א׳', 'יום ב׳', 'יום ג׳', 'יום ד׳', 'יום ה׳', 'יום ו׳', 'יום שבת'];

// Accepts ISO 'YYYY-MM-DD' (parsed as LOCAL midnight — a UTC parse would land
// on the previous evening in Israel) or a Date. Anything else falls back to
// formatDateShort's own policy — the original text, never a blank — so a bad
// bound is visible rather than a sentence with a hole in it.
export function formatDateWithWeekday(input) {
  if (input === null || input === undefined || input === '') return null;
  const d = input instanceof Date
    ? input
    : /^\d{4}-\d{2}-\d{2}$/.test(String(input)) ? new Date(`${input}T00:00:00`) : null;
  if (!d || isNaN(d.getTime())) return formatDateShort(input);
  return `${HEBREW_DAYS[d.getDay()]} ${formatDateShort(d)}`;
}

// ── Document dates vs. filter ranges ────────────────────────────────────────
//
// The two date vocabularies of this app meet here. Documents carry the DB's
// 'DD/MM/YYYY' text; every date FILTER (PeriodPicker, utils/period.js) speaks
// ISO 'YYYY-MM-DD'. period.js states outright that the slash format never
// appears at its layer, so the bridge belongs on this side.
//
// Comparison is lexicographic on a zero-padded 'YYYYMMDD' key — the same shape
// the server sorts document dates with (substr(date,7,4)||substr(date,4,2)||
// substr(date,1,2)). The padding is load-bearing: '5/6/2026' unpadded yields
// '202665', which sorts after '20261231'. The OCR does emit single-digit
// days/months.
export function ymdKey(ddmmyyyy) {
  if (!ddmmyyyy) return '';
  const [d, m, y] = String(ddmmyyyy).split('/');
  if (!d || !m || !y) return '';
  return `${y}${m.padStart(2, '0')}${d.padStart(2, '0')}`;
}

// Is a 'DD/MM/YYYY' document date inside an ISO range? Either bound may be
// empty, meaning "unbounded on that side".
//
// An UNDATED (or unparseable) document is IN every range, deliberately. The
// alternative hides a note the OCR failed to date behind a filter the user
// cannot see it through — and the callers that must place a document in a
// specific month (paidDocsInMonth) exclude it themselves, before asking.
export function isDateInRange(docDate, isoFrom, isoTo) {
  const k = ymdKey(docDate);
  if (!k) return true;
  const from = isoFrom ? String(isoFrom).replace(/-/g, '') : '';
  const to   = isoTo   ? String(isoTo).replace(/-/g, '')   : '';
  if (from && k < from) return false;
  if (to   && k > to)   return false;
  return true;
}

// Parse an upload timestamp into a Date. Accepts a SQLite 'YYYY-MM-DD HH:MM:SS'
// value (stored in UTC — `created_at` on documents), an ISO string, or a Date.
// Returns null for empty/unparseable input. Kept separate from formatDateShort
// because upload timestamps carry a real time-of-day we want to preserve.
function parseTimestamp(input) {
  if (input === null || input === undefined || input === '') return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  // A bare 'YYYY-MM-DD HH:MM:SS' is UTC (SQLite CURRENT_TIMESTAMP); append 'Z'
  // so it isn't parsed as local. Anything already carrying T/offset is left as-is.
  const iso = s.includes('T') || !s.includes(' ') ? s : s.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

// Upload timestamp → local-timezone date only (dd/mm/yy), for a table cell.
// Returns null on empty/unparseable input (callers add their own '—' fallback).
export function formatUploadedShort(input) {
  const d = parseTimestamp(input);
  if (!d) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

// Upload timestamp → full local date + time, for a hover tooltip. toLocaleString
// renders in the viewer's own timezone and locale (Intl), so the hour shown
// matches the user's browser/OS clock. Returns undefined on empty/unparseable
// input so a `title={...}` attribute simply renders no tooltip.
export function formatUploadedFull(input) {
  const d = parseTimestamp(input);
  if (!d) return undefined;
  // Built from parts: toLocaleString('he-IL') writes the date with DOTS, and
  // dates are DD/MM/YY with slashes everywhere (design-system C20).
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const part = (type) => parts.find((x) => x.type === type)?.value;
  return `${part('day')}/${part('month')}/${part('year')}, ${part('hour')}:${part('minute')}`;
}

// An ISO 'YYYY-MM-DD' range as 'DD/MM/YY-DD/MM/YY', for the date line in a
// hover readout and for any card that titles a period by its bounds.
//
// Split from the string rather than routed through formatDateShort on purpose:
// that helper hands 'YYYY-MM-DD' to `new Date`, which reads it as UTC midnight,
// and then asks for LOCAL date parts. Correct in Israel (UTC+2/+3), a day early
// from anywhere behind UTC — and a range bound that is off by one is a wrong
// answer, not a cosmetic one. Non-ISO input falls back to formatDateShort's own
// policy (return the original text) so a bad bound stays visible.
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatIsoDayShort(iso) {
  const m = String(iso ?? '').match(ISO_DAY);
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : formatDateShort(iso);
}

export function formatIsoRangeShort(from, to) {
  if (!from || !to) return null;
  return `${formatIsoDayShort(from)}-${formatIsoDayShort(to)}`;
}

// SQLite's CURRENT_TIMESTAMP: UTC, but written with no zone marker, which
// `new Date` reads as the BROWSER's local time — so a row created at 01:30 on
// the 1st in Israel (22:30 UTC the day before) rendered as the day before.
const ZONELESS_UTC_RE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * DD/MM/YY, matching how dates already read across the product — the ISRAEL
 * calendar day, the same day the server counts it on (server/lib/israelDay.js).
 */
export function formatUtcDay(value) {
  if (!value) return '—';
  const s = String(value);
  const d = new Date(ZONELESS_UTC_RE.test(s) ? `${s.replace(' ', 'T')}Z` : s);
  if (Number.isNaN(d.getTime())) return '—';
  // Built from parts, not toLocaleDateString('he-IL'): the Hebrew locale
  // writes DOTS (01.09.30), and the rule is DD/MM/YY with slashes everywhere,
  // the Command Center included (design-system C20).
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'Asia/Jerusalem',
  }).formatToParts(d);
  const part = (type) => parts.find((x) => x.type === type)?.value;
  return `${part('day')}/${part('month')}/${part('year')}`;
}

